import type { BaseEmoji } from 'emoji-mart';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmojiSelect } from '@/pages/Schedule/components';
import type { ExperienceFeed } from '@/types/gql.generated';
import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@/ui';

type Props = {
  feed: ExperienceFeed;
  // Receives the chosen emoji, or null when it's cleared. Each row wires this to
  // its own update hook (feeds vs sources), mirroring FeedCategory.
  onChange: (emoji: string | null) => void;
  isReadOnly?: boolean;
};

// Sits in the same row shape as the FeedCategory and FeedLabels rows beside it —
// a 20px leading icon, gap 3, 34px tall — and takes its control styling from the
// schedule-side equivalent this panel is modelled on
// (pages/Schedule/.../SourceDefaults/components/EmojiPicker): a compact filled
// square holding the emoji, grayscaled to a placeholder when there is none.
//
// gray.100/200 rather than an `ah-` surface token, deliberately: those resolve to
// a lavender-tinted gray, and this square has to sit level with the neutral gray
// the controls beside it fill with. Don't "modernize" it to surface.panel.
//
// The feed's default emoji: applied across its events, over any emoji they
// arrived with (see features/events/lib/eventEmoji.lib.ts).
export const FeedEmoji = ({ feed, onChange, isReadOnly }: Props) => {
  // With an emoji assigned the menu offers Remove / Change rather than opening
  // straight into the picker, so a square with no room for a clear affordance
  // still has one. Same two-step the schedule-side control uses.
  const [forceShowPicker, setForceShowPicker] = useState(false);
  const { t } = useTranslation('sources', { keyPrefix: 'source_defaults' });
  const emoji = feed.emoji ?? null;

  if (isReadOnly) {
    if (!emoji) {
      return null;
    }

    return (
      <Flex align="center" gap="3" minH="34px" w="100%">
        <EmojiRowIcon />
        <Text fontSize="18px" lineHeight="1">
          {emoji}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex align="center" gap="3" minH="34px" w="100%">
      <EmojiRowIcon />
      <Menu
        closeOnSelect={false}
        isLazy
        onClose={() => setForceShowPicker(false)}
      >
        {({ onClose }) => (
          <>
            <MenuButton
              _hover={{ bg: 'gray.200' }}
              as={Button}
              bg="gray.100"
              borderRadius="ah-md"
              fontSize="18px"
              fontWeight="normal"
              h="34px"
              minW="auto"
              p="0"
              variant="ghost"
              w="34px"
              aria-label={
                emoji ? 'Change default emoji' : 'Add a default emoji'
              }
            >
              <Box filter={emoji ? 'none' : 'grayscale(100%)'}>
                {emoji ?? '😄'}
              </Box>
            </MenuButton>

            <MenuList>
              {emoji && !forceShowPicker ? (
                <>
                  <MenuItem
                    onClick={() => {
                      setForceShowPicker(false);
                      onChange(null);
                      onClose();
                    }}
                  >
                    {t('emoji_picker.remove')}
                  </MenuItem>
                  <MenuItem onClick={() => setForceShowPicker(true)}>
                    {t('emoji_picker.change')}
                  </MenuItem>
                </>
              ) : (
                <EmojiSelect
                  onSelect={(selected: BaseEmoji) => {
                    onChange(selected.native);
                    setForceShowPicker(false);
                    onClose();
                  }}
                />
              )}
            </MenuList>
          </>
        )}
      </Menu>
    </Flex>
  );
};

// The row's leading icon. Pinned to a 20px slot rather than left to the glyph's
// own advance width, so it lands on the same edge as CategoryIcon (a 20px image)
// and the label icons beside it.
const EmojiRowIcon = () => (
  <Text
    aria-hidden
    flexShrink={0}
    fontSize="18px"
    lineHeight="1"
    textAlign="center"
    w="20px"
  >
    🙂
  </Text>
);
